import apiRequest from "src/utils/api-request";
import withApiPath from "src/utils/with-api-path";

export interface PoolWithdrawal {
	id: number;
	uuid: string;
	status: string;
	outboundWalletId: number;
	walletAddress: string;
	blockchain: string;
	recipientAddress: string;
	recipientAddressTag?: string;
	currency: string;
	amount: string;
	blockchainFee?: string;
	usdAmount: string;
	requiresApproval: boolean;
	approval?: ApprovalInfo;
	transactionId?: number;
	transactionHash?: string;
	purpose: string;
	notes?: string;
	failureReason?: string;
	rejectionReason?: string;
	createdBy: AdminInfo;
	createdAt: string;
	updatedAt: string;
	completedAt?: string;
}

export interface ApprovalInfo {
	id: number;
	uuid: string;
	status: string;
	expiresAt: string;
	approvedBy?: AdminInfo;
	approvedAt?: string;
	notes?: string;
	rejectionReason?: string;
}

export interface AdminInfo {
	id: number;
	email: string;
	name?: string;
}

export interface CreatePoolWithdrawalParams {
	outboundWalletId: number;
	recipientAddress: string;
	currency: string;
	amount: string;
	purpose: string;
	totpCode: string;
}

export interface ApproveWithdrawalParams {
	withdrawalId: number;
	notes?: string;
	totpCode: string;
}

export interface RejectWithdrawalParams {
	withdrawalId: number;
	reason: string;
}

export interface CancelWithdrawalParams {
	withdrawalId: number;
	reason?: string;
}

export interface ListPoolWithdrawalsParams {
	outboundWalletId?: number;
	status?: string;
	blockchain?: string;
	limit?: number;
	offset?: number;
}

export interface PaginatedPoolWithdrawalsResponse {
	withdrawals: PoolWithdrawal[];
	pagination: {
		totalCount: number;
		limit: number;
		offset: number;
	};
}

const poolWithdrawalProvider = {
	async createWithdrawal(
		params: CreatePoolWithdrawalParams,
	): Promise<PoolWithdrawal> {
		const response = await apiRequest.post(
			withApiPath("/pool-withdrawals"),
			{
				walletId: params.outboundWalletId,
				recipientAddress: params.recipientAddress,
				currency: params.currency,
				amount: params.amount,
				purpose: params.purpose,
			},
			{
				headers: {
					"X-2FA-Code": params.totpCode,
				},
			},
		);
		return response.data;
	},

	async listWithdrawals(
		params: ListPoolWithdrawalsParams = {},
	): Promise<PaginatedPoolWithdrawalsResponse> {
		const response = await apiRequest.get(withApiPath("/pool-withdrawals"), {
			params: {
				walletId: params.outboundWalletId,
				status: params.status,
				blockchain: params.blockchain,
				limit: params.limit ?? 50,
				offset: params.offset ?? 0,
			},
		});
		return response.data;
	},

	async listPendingApprovals(
		limit = 50,
		offset = 0,
	): Promise<PaginatedPoolWithdrawalsResponse> {
		const response = await apiRequest.get(
			withApiPath("/pool-withdrawals/pending-approvals"),
			{
				params: { limit, offset },
			},
		);
		return response.data;
	},

	async getWithdrawal(id: number): Promise<PoolWithdrawal> {
		const response = await apiRequest.get(
			withApiPath(`/pool-withdrawals/${id}`),
		);
		return response.data;
	},

	async approveWithdrawal(
		params: ApproveWithdrawalParams,
	): Promise<PoolWithdrawal> {
		const response = await apiRequest.post(
			withApiPath(`/pool-withdrawals/${params.withdrawalId}/approve`),
			{
				notes: params.notes,
			},
			{
				headers: {
					"X-2FA-Code": params.totpCode,
				},
			},
		);
		return response.data;
	},

	async rejectWithdrawal(
		params: RejectWithdrawalParams,
	): Promise<PoolWithdrawal> {
		const response = await apiRequest.post(
			withApiPath(`/pool-withdrawals/${params.withdrawalId}/reject`),
			{
				reason: params.reason,
			},
		);
		return response.data;
	},

	async cancelWithdrawal(
		params: CancelWithdrawalParams,
	): Promise<PoolWithdrawal> {
		const response = await apiRequest.post(
			withApiPath(`/pool-withdrawals/${params.withdrawalId}/cancel`),
			{
				reason: params.reason,
			},
		);
		return response.data;
	},
};

export default poolWithdrawalProvider;
